import { useEffect, useMemo, useState } from 'react';
import type { CoverageMatrixVM } from './useCoverageMatrix';
import type { MatrixRoleRow } from './coverageMatrixHelpers';

/** The role rail's buckets. Mirrors `CoverageTab` on the app axis, read the
 *  other way round: a role "needs attention" when some app has lost the role
 *  it was linked to, and is "uncovered" when it reaches no app at all. */
export type RoleAxisTab = 'all' | 'needsAttention' | 'uncovered';

/**
 * EAA-1817: state for the **Theo vai trò** view — frame 55 read from the other
 * side. Where the per-app board asks "what is this app missing", this asks
 * "where does this standard role actually land".
 *
 * WHY IT IS THIS SMALL. It derives nothing: `useCoverageMatrix` already builds
 * one row per standard role carrying that role's cell for every app, which IS
 * the role-first view's data. This hook only owns the rail's own filter state
 * and which role is open. Recomputing the grid a second time, transposed, is
 * exactly the duplication that left this screen with two matrices before.
 *
 * WHY THERE IS NO FRAME TO POINT AT. Frame 55's switcher has offered "Theo vai
 * trò" since 2026-08-11 but the pack never drew it, so the layout here is
 * frame 55's own — same rail, same table shell — with the axes swapped. What it
 * deliberately does NOT do is grow a second chip editor: editing stays in the
 * per-app board, and a row here links across to it. A per-app-role picker on
 * every row of this table would need one `GET /admin/app-roles?application=…`
 * per visible app, and would put a second editable surface for the same
 * `RoleMap` rows in the app — two places to drift apart, which is the defect
 * EAA-1841 merged these routes to close.
 */
export function useRoleAxisBoard(matrix: CoverageMatrixVM) {
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<RoleAxisTab>('all');
    const [selectedRoleKey, setSelectedRoleKey] = useState<string | null>(null);

    const roleRows = matrix.allRows;

    const needsAttentionCount = useMemo(
        () => roleRows.filter((row) => row.hasPaused).length,
        [roleRows]
    );
    const uncoveredCount = useMemo(
        () => roleRows.filter((row) => row.coveredCount === 0).length,
        [roleRows]
    );

    const normalizedSearch = search.trim().toLowerCase();
    const visibleRows = useMemo(
        () =>
            roleRows.filter((row) => {
                if (tab === 'needsAttention' && !row.hasPaused) return false;
                if (tab === 'uncovered' && row.coveredCount > 0) return false;
                if (!normalizedSearch) return true;
                return `${row.roleKey} ${row.roleName}`.toLowerCase().includes(normalizedSearch);
            }),
        [roleRows, tab, normalizedSearch]
    );

    // Never leave the detail pane blank on first load — the frame always shows
    // one row already open. Never overrides a caller's own click.
    useEffect(() => {
        if (selectedRoleKey !== null) return;
        if (roleRows.length === 0) return;
        setSelectedRoleKey(roleRows[0].roleKey);
    }, [roleRows, selectedRoleKey]);

    const selectedRow: MatrixRoleRow | null = useMemo(
        () => roleRows.find((row) => row.roleKey === selectedRoleKey) ?? null,
        [roleRows, selectedRoleKey]
    );

    return {
        search,
        setSearch,
        tab,
        setTab,
        roleRows,
        visibleRows,
        needsAttentionCount,
        uncoveredCount,
        selectedRoleKey,
        selectedRow,
        selectRole: setSelectedRoleKey,
    };
}

export type RoleAxisBoardVM = ReturnType<typeof useRoleAxisBoard>;
